import './floorpages.css';
import useScrollToTop from '../components/useScrollToTop';
import ReusableLanding from '../components/ReusableLanding';
import Button from '../components/Button';

// Import image assets (assuming you have these in your project)
import twoBHKLayout1 from '../img/floor_plan_6.avif';
import houseplan from '../img/floor_plan_5.avif';

const MapPass = () => {
    useScrollToTop();

    return (
        <>
        <ReusableLanding
        imageUrl={twoBHKLayout1}
        title="Building Your Dream Home in Patna? Here's Your Simple Guide to Getting Your House Plan Approved!"
        description="So, you're dreaming of building your own home in our vibrant Patna! That's fantastic! But before you lay a single brick, there's a crucial first step: getting your house plan officially approved. Don't worry, it might sound a bit complicated, but we're here to break it down for you in plain and simple terms. Think of it as getting permission before you start your exciting construction journey."
    />
    <div className='project-container'>
    <div className="project-details">
     <section className="">
  <h2 className=" ">Step 1: Finding Your "Building Zone" in Patna - Knowing Where to Knock First</h2>
  <p className="">
 Patna is a big city, and just like different neighborhoods have different post offices, different areas might fall under different government offices for building approvals. Here's how to figure out your zone:
  </p>

  <ul className="list-items">
  <li>
  <strong>If Your Land is in the Heart of Patna City:</strong> You'll likely need to head to the <strong>Patna Municipal Corporation (PMC).</strong> Think of them as the main "city hall" for all things within Patna's main boundaries.
  </li>
  <li>
  <strong>If Your Land is in the Growing Areas Around Patna:</strong>  For areas a little outside the main city but becoming part of the greater Patna region, the <strong>Patna Metropolitan Area Authority (PMAA)</strong>  might be your first stop. They oversee planned development in the wider Patna area.
  </li>
  <li>
  <strong>If Your Land is in a More Rural Part or on the Outskirts</strong> Your local <strong>Block Development Office (BDO)</strong> or <strong>Circle Office</strong>  might be the place to start. These are like local government hubs that can guide you, especially in areas further from the city center.
  </li>
  </ul>
  <p>
    Not sure which zone you're in? A good first step is often to contact the <strong>Patna Municipal Corporation (PMC).</strong> They can usually tell you if your area falls under their jurisdiction or if you need to contact the PMAA or your local BDO/Circle Office. Checking your property papers or asking neighbors can also give you clues!
  </p>
  </section>

<section className="">
  <h2 className=" ">Step 2: Getting Your "Building Blocks" Ready - The Documents You'll Need</h2>
  <p className="">
    Once you know where to go, it's time to gather your essential papers. Think of these as the ingredients for your application:
  </p>
  <ul className="list-items">
    <li>
      <strong>Proof of Ownership:</strong> Your <strong>sale deed (registry papers)</strong> or <strong>mutation certificate (दाखिल खारिज)</strong> – these show you legally own the land. Keep both originals and copies handy.
    </li>
    <li>
      <strong>Your ID:</strong> Your <strong>Aadhar card</strong>, <strong>voter ID</strong>, or any official government ID.
    </li>
    <li>
      <strong>The House Plan (नक्शा):</strong> This is the detailed drawing of your dream home, made by a <strong>licensed architect or engineer</strong>. It's not just a sketch; it has to follow specific rules!
      <img src={houseplan} alt="houseplan in Patna, Bihar" className="project-image" />
    </li>
    <li>
      <strong>Site Plan:</strong> A drawing showing exactly where your house will sit on your land, including boundaries and nearby roads.
    </li>
    <li>
      <strong>Sometimes, a "No Objection" from Neighbors:</strong> In some cases, you might need your neighbors to sign a paper saying they're okay with your plan.
    </li>
    <li>
      <strong>Other Papers:</strong> Depending on the rules, you might need things like soil test reports or structural safety certificates. The office you visit will give you a full list.
    </li>
  </ul>
</section>

<section className="">
  <h2 className=" ">Step 3: Understanding the "Building Rules" - Making Sure Your Plan Fits</h2>
  <p className="">
    Your house plan needs to follow certain guidelines to ensure everyone's safety and well-being. Think of these as the rules of the game:
  </p>
  <ul className="list-items">
    <li>
      <strong>Leaving Space (Setbacks):</strong> You'll need to leave a certain amount of open space around your house.
    </li>
    <li>
      <strong>How Much You Can Build (Coverage Area & Floor Area Ratio - FAR):</strong> There are rules about how much of your land you can cover with your building and the total size of your house compared to your plot.
    </li>
    <li>
      <strong>Height Limits:</strong> There might be restrictions on how tall your house can be.
    </li>
    <li>
      <strong>Parking Space:</strong> You might need to show space for parking your vehicles.
    </li>
    <li>
      <strong>Light and Air (Ventilation & Lighting):</strong> Your plan needs to ensure your house will have enough natural light and fresh air.
    </li>
    <li>
      <strong>Proper Sanitation:</strong> Plans for toilets, drainage, and waste disposal are important.
    </li>
    <li>
      <strong>Safety First (Structural Safety):</strong> Your house design needs to be strong and safe.
    </li>
    <li>
      <strong>Local Bylaws:</strong> These are the specific rules for building in Patna set by the PMC, PMAA, or other local authorities.
    </li>
  </ul>
</section>

<section className="">
  <h2 className=" ">Step 4: Making Your "Request" - Submitting Your Application</h2>
  <p className="">
    You've got your documents and a plan that (hopefully!) follows the rules. Now it's time to submit your application:
  </p>
  <ul className="list-items">
    <li>
      <strong>Find the Right Office:</strong> Head to the "building plan approval" or "town planning" department at the PMC/PMAA or ask at your local BDO/Circle Office.
    </li>
    <li>
      <strong>Get the Form:</strong> Ask for the official application form.
    </li>
    <li>
      <strong>Fill It Carefully:</strong> Provide all the information accurately.
    </li>
    <li>
      <strong>Hand in Your Papers:</strong> Attach all your documents to the form. Keep copies for yourself!
    </li>
    <li>
      <strong>Pay the Fees:</strong> There will be a fee to process your application. Ask about the amount and how to pay, and always get a receipt.
    </li>
    <li>
      <strong>Keep Track:</strong> Note down the date you submitted, who you spoke to, and any reference number you get.
    </li>
  </ul>
</section>

<section className="">
  <h2 className=" ">What Happens Next? The Waiting Game and What to Expect</h2>
  <p className="">
    After you submit your application, the officials will carefully review everything. They might even come to visit your land. If they find any issues or have questions, they'll let you know, and you'll need to address them.
  </p>
  <ul className="list-items">
    <li>
      <strong>Scrutiny and Verification:</strong> They'll check if all your documents are in order and if your plan follows the building rules.
    </li>
    <li>
      <strong>Site Inspection (Likely):</strong> Officials might visit your land to verify the details in your plan.
    </li>
    <li>
      <strong>Addressing Objections or Queries:</strong> If there are any problems with your application, you'll be asked to provide more information or revise your plan.
    </li>
    <li>
      <strong>Approval or Rejection:</strong> If everything is okay, you'll get a <strong>building permit</strong>. If not, you'll receive a rejection letter explaining why.
    </li>
    <li>
      <strong>Post-Approval Procedures:</strong> Remember to build according to the approved plan. There might be inspections during construction, and you'll likely need a <strong>completion certificate</strong> once you're finished.
    </li>
  </ul>
  <p>
    <strong>A Simple Tip:</strong> Don't hesitate to ask questions at the government offices. Be polite and patient – they are there to help guide you through the process. Getting your house plan approved the right way is a vital step towards building a safe and sound home for you and your family in Patna! Take it one step at a time, and you'll get there!
  </p>
</section>
<div className="">
          <a href="https://wa.me/918828531570" target="_blank" rel="noopener noreferrer">
            <Button className="button-outlined">Have Questions? Call Us</Button>
          </a>
        </div>
    
    </div>
    </div>

    </>
    );

}

export default MapPass;

